"use client";

import "./Home.module.css";
import { AlertCircle, ArrowRight, BadgeCheck, Check, ChevronDown, FileText, MapPin, Phone, ShieldCheck, Timer } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import EnTete from "@/reusable_sections/Header";
import NosServices from "./sections/NosServices";
import AidesFinancieres from "./sections/AidesFinancieres";
import PourquoiChoisirBeTravaux from "./sections/PourquoiChoisirBeTravaux";
import TransformationsElegantes from "./sections/TransformationsElegantes";
import ServicesUrgence from "./sections/ServicesUrgence";
import PourLesProfessionnels from "./sections/PourLesProfessionnels";
import RecevoirMonDevisPersonnalise from "./sections/RecevoirMonDevisPersonnalise";
import PiedDePage from "@/reusable_sections/Footer";
import { Button } from "@/components/ui/button";
const HOME_PREFILL_KEY = "bt_home_lead_prefill";
const HOME_URGENCE_KEY = "bt_home_urgence_prefill";
const travauxOptions = [{
  label: "Plomberie",
  slug: "plomberie"
}, {
  label: "Electricite",
  slug: "electricite"
}, {
  label: "Chauffage",
  slug: "chauffage"
}, {
  label: "Climatisation / PAC",
  slug: "climatisation"
}, {
  label: "Photovoltaique",
  slug: "photovoltaique"
}, {
  label: "Isolation",
  slug: "isolation"
}, {
  label: "Menuiseries",
  slug: "menuiseries"
}, {
  label: "Couverture / Toiture",
  slug: "couverture-toiture"
}, {
  label: "Carrelage",
  slug: "carrelage"
}, {
  label: "Maconnerie",
  slug: "maconnerie"
}, {
  label: "Renovation interieure",
  slug: "renovation-interieure"
}, {
  label: "Renovation exterieure",
  slug: "renovation-exterieure"
}, {
  label: "Peinture / Facade",
  slug: "peinture-facade"
}, {
  label: "Serrurerie",
  slug: "serrurerie"
}, {
  label: "Depannage urgent",
  slug: "depannage-urgent"
}];
function PremiumHomeEntry() {
  const [leadName, setLeadName] = useState("");
  const [leadPhone, setLeadPhone] = useState("");
  const [leadCategory, setLeadCategory] = useState(travauxOptions[0].slug);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [urgencyPhone, setUrgencyPhone] = useState("");
  const [urgencyZip, setUrgencyZip] = useState("");
  const categoryRef = useRef<HTMLDivElement | null>(null);
  const selectedCategory = useMemo(() => travauxOptions.find(item => item.slug === leadCategory) || travauxOptions[0], [leadCategory]);
  useEffect(() => {
    function handleOutsideClick(event: MouseEvent) {
      if (!categoryRef.current) return;
      if (!categoryRef.current.contains(event.target as Node)) {
        setIsCategoryOpen(false);
      }
    }
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsCategoryOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);
  function redirectToQuote() {
    if (typeof window === "undefined") return;
    const payload = {
      name: leadName.trim(),
      phone: leadPhone.trim(),
      category: selectedCategory.label,
      slug: selectedCategory.slug,
      source: "home-premium",
      ts: Date.now()
    };
    try {
      window.localStorage.setItem(HOME_PREFILL_KEY, JSON.stringify(payload));
    } catch (_) {}
    window.location.href = `https://betravaux.com/demande-de-devis/${selectedCategory.slug}`;
  }
  function redirectToUrgence() {
    if (typeof window === "undefined") return;
    const payload = {
      phone: urgencyPhone.trim(),
      zip: urgencyZip.trim(),
      source: "home-premium",
      ts: Date.now()
    };
    try {
      window.localStorage.setItem(HOME_URGENCE_KEY, JSON.stringify(payload));
    } catch (_) {}
    const params = new URLSearchParams();
    if (payload.phone) params.set("tel", payload.phone);
    if (payload.zip) params.set("cp", payload.zip);
    const query = params.toString();
    window.location.href = `https://betravaux.com/depannage/${query ? `?${query}` : ""}`;
  }
  return <section className="relative min-h-screen overflow-hidden bg-slate-950 pt-16">
      <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
      backgroundImage: "url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1442601/img/modern_home_renovation_individual_professional_journey.png)"
    }} />
      <div className="absolute inset-0 bg-black/55" />

      <div className="relative z-10 px-4 pb-14 pt-14 sm:px-6 lg:px-8 lg:pt-24">
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto max-w-4xl text-center">
            <h1 className="text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-7xl">
              Des artisans qualifies,
              <br />
              des projets securises
            </h1>
            <p className="mx-auto mt-6 max-w-3xl text-lg text-white/85 sm:text-xl">
              Be Travaux simplifie la recherche d&apos;artisans qualifies pour tous vos projets.
            </p>
          </div>

          <div className="mx-auto mt-12 grid max-w-5xl gap-6 lg:grid-cols-2">
            <div className="rounded-[28px] border border-orange-200 bg-white/98 p-6 shadow-[0_24px_70px_rgba(0,0,0,0.22)] backdrop-blur md:p-7">
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-500 text-white shadow-lg">
                  <FileText className="h-7 w-7" />
                </div>
                <div>
                  <div className="text-4xl font-bold tracking-tight text-orange-500">Devis gratuit</div>
                  <p className="mt-2 text-base leading-6 text-slate-600">
                    Decrivez votre projet en quelques secondes, nous vous recontactons rapidement.
                  </p>
                </div>
              </div>

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                  <span className="text-slate-400">
                    <FileText className="h-4 w-4" />
                  </span>
                  <input type="text" value={leadName} onChange={e => setLeadName(e.target.value)} placeholder="Votre nom" autoComplete="name" className="w-full bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400" />
                </label>

                <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                  <span className="text-slate-400">
                    <Phone className="h-4 w-4" />
                  </span>
                  <input type="tel" value={leadPhone} onChange={e => setLeadPhone(e.target.value)} placeholder="Votre telephone" autoComplete="tel" className="w-full bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400" />
                </label>
              </div>

              <div className="mt-3" ref={categoryRef}>
                <button type="button" onClick={() => setIsCategoryOpen(prev => !prev)} className={`flex w-full items-center gap-3 rounded-2xl border px-4 py-4 text-left shadow-sm transition ${isCategoryOpen ? "border-orange-300 bg-orange-50/80 shadow-[0_12px_32px_rgba(249,115,22,0.12)]" : "border-orange-200 bg-white hover:border-orange-300 hover:bg-orange-50/40"}`} aria-expanded={isCategoryOpen} aria-haspopup="listbox">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-100 text-orange-500">
                    <BadgeCheck className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-orange-500/80">
                      Type de travaux
                    </div>
                    <div className="truncate text-lg font-semibold text-slate-800">{selectedCategory.label}</div>
                  </div>
                  <ChevronDown className={`h-5 w-5 flex-shrink-0 text-orange-500 transition ${isCategoryOpen ? "rotate-180" : ""}`} />
                </button>

                {isCategoryOpen ? <div className="mt-3 overflow-hidden rounded-[24px] border border-orange-200 bg-white shadow-[0_18px_44px_rgba(15,23,42,0.14)]">
                    <div className="border-b border-orange-100 bg-gradient-to-r from-orange-50 to-white px-4 py-3">
                      <div className="text-sm font-semibold text-slate-800">Choisissez votre metier</div>
                      <div className="text-xs text-slate-500">La redirection reste branchee sur la bonne categorie.</div>
                    </div>
                    <div className="grid max-h-72 overflow-y-auto p-3 sm:grid-cols-2">
                      {travauxOptions.map(option => {
                    const isActive = option.slug === leadCategory;
                    return <button key={option.slug} type="button" onClick={() => {
                      setLeadCategory(option.slug);
                      setIsCategoryOpen(false);
                    }} className={`flex items-center gap-3 rounded-2xl px-3 py-3 text-left transition ${isActive ? "bg-orange-500 text-white shadow-lg" : "text-slate-700 hover:bg-orange-50 hover:text-orange-600"}`}>
                            <span className={`flex h-8 w-8 items-center justify-center rounded-xl ${isActive ? "bg-white/20 text-white" : "bg-orange-100 text-orange-500"}`}>
                              {isActive ? <Check className="h-4 w-4" /> : <BadgeCheck className="h-4 w-4" />}
                            </span>
                            <span className="text-sm font-medium">{option.label}</span>
                          </button>;
                  })}
                    </div>
                  </div> : null}
              </div>

              <Button onClick={redirectToQuote} className="mt-5 h-14 w-full rounded-2xl bg-orange-500 text-base font-semibold text-white shadow-lg transition hover:bg-orange-600">
                Obtenir mon devis gratuit
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-500">
                <ShieldCheck className="h-4 w-4" />
                <span>Vos informations sont 100% securisees</span>
              </div>
            </div>

            <div className="rounded-[28px] border border-red-200 bg-white/98 p-6 shadow-[0_24px_70px_rgba(0,0,0,0.22)] backdrop-blur md:p-7">
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-red-500 text-white shadow-lg">
                  <AlertCircle className="h-7 w-7" />
                </div>
                <div>
                  <div className="text-4xl font-bold tracking-tight text-red-500">Urgence ?</div>
                  <div className="text-4xl font-bold tracking-tight text-slate-900">Intervention rapide</div>
                  <p className="mt-2 text-base leading-6 text-slate-600">
                    Un probleme urgent ? Nous intervenons 7j/7 dans les plus brefs delais.
                  </p>
                </div>
              </div>

              <div className="mt-6 grid gap-3">
                <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                  <span className="text-slate-400">
                    <Phone className="h-4 w-4" />
                  </span>
                  <input type="tel" value={urgencyPhone} onChange={e => setUrgencyPhone(e.target.value)} placeholder="Votre telephone" autoComplete="tel" className="w-full bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400" />
                </label>

                <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                  <span className="text-slate-400">
                    <MapPin className="h-4 w-4" />
                  </span>
                  <input type="text" value={urgencyZip} onChange={e => setUrgencyZip(e.target.value)} placeholder="Votre code postal" autoComplete="postal-code" className="w-full bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400" />
                </label>
              </div>

              <Button onClick={redirectToUrgence} className="mt-5 h-14 w-full rounded-2xl bg-red-500 text-base font-semibold text-white shadow-lg transition hover:bg-red-600">
                Lancer mon depannage
                <Phone className="ml-2 h-5 w-5" />
              </Button>

              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-500">
                <Timer className="h-4 w-4" />
                <span>Reponse en moins de 15 min</span>
              </div>
            </div>
          </div>

          <div className="mx-auto mt-7 grid max-w-5xl gap-0 overflow-hidden rounded-[24px] border border-white/60 bg-white/95 shadow-[0_18px_60px_rgba(0,0,0,0.16)] backdrop-blur md:grid-cols-4">
            {[["Artisans qualifies", "Verifies et notes", ShieldCheck], ["Devis gratuit", "Sans engagement", FileText], ["Intervention rapide", "Delais respectes", Timer], ["Garantie decennale", "Travaux assures", BadgeCheck]].map(([title, subtitle, Icon], index) => <div key={title} className={`flex items-center gap-4 px-5 py-5 ${index < 3 ? "border-b border-slate-200 md:border-b-0 md:border-r" : ""}`}>
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-50 text-orange-500">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-900">{title}</div>
                  <div className="text-sm text-slate-500">{subtitle}</div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}
export default function HomePage() {
  return <div>
      <EnTete />
      <PremiumHomeEntry />
      <NosServices />
      <AidesFinancieres />
      <PourquoiChoisirBeTravaux />
      <TransformationsElegantes />
      <ServicesUrgence />
      <PourLesProfessionnels />
      <RecevoirMonDevisPersonnalise />
      <PiedDePage />
    </div>;
}